import { useState } from 'react'
import { Trash2, Building2, Home, DollarSign, ChevronDown, ChevronUp } from 'lucide-react'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { cn } from '../../lib/utils'

export type PropertyType = 'dst' | 'like-kind'

export interface DstPortfolioProperty {
  address: string
  city?: string
  state?: string
}

export interface IdentifiedProperty {
  address: string
  estimatedFMV: number
  type: PropertyType
  allocationAmount?: number
  dstListingId?: string
  dstTitle?: string
  description?: string
  commonName?: string
  legalDescription?: string
  taxParcelNumber?: string
  cityCountyState?: string
  percentageInterest?: number
  dollarValueAcquired?: number
  investmentAmount?: number
  totalOfferingSize?: number
  properties?: DstPortfolioProperty[]
  selectedPropertyAddresses?: string[]
}

interface IdentificationPropertyRowProps {
  property: IdentifiedProperty
  index: number
  showAllocationInput: boolean
  salesPrice: number | null
  disabled: boolean
  onUpdate: (updated: IdentifiedProperty) => void
  onRemove: () => void
}

export type { IdentificationPropertyRowProps }

const TYPE_BADGE: Record<PropertyType, { label: string; className: string }> = {
  dst: {
    label: 'DST',
    className:
      'inline-flex items-center gap-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 text-[10px] font-semibold',
  },
  'like-kind': {
    label: 'Like-Kind',
    className:
      'inline-flex items-center gap-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 text-[10px] font-semibold',
  },
}

const PROPERTY_TYPE_OPTIONS = [
  { value: 'single-family', label: 'Single Family' },
  { value: 'multi-family', label: 'Multi-Family' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'land', label: 'Land' },
  { value: 'other', label: 'Other' },
]

export function IdentificationPropertyRow({
  property,
  index,
  showAllocationInput,
  salesPrice,
  disabled,
  onUpdate,
  onRemove,
}: IdentificationPropertyRowProps) {
  const [partialAcquisition, setPartialAcquisition] = useState(
    !!(property.percentageInterest && property.percentageInterest < 100)
  )
  const badge = TYPE_BADGE[property.type]
  const Icon = property.type === 'dst' ? Building2 : Home
  const isDst = property.type === 'dst'
  const isLikeKind = property.type === 'like-kind'
  const investmentValue = property.investmentAmount ?? property.estimatedFMV
  const percentOfDst =
    isDst && property.totalOfferingSize && property.totalOfferingSize > 0
      ? (investmentValue / property.totalOfferingSize) * 100
      : null
  const portfolioProperties = property.properties ?? []
  const isPortfolio = isDst && portfolioProperties.length > 1

  return (
    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-blue-50/30 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800">
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center text-[10px] font-bold mt-1">
        {index + 1}
      </span>
      <div className="flex-1 space-y-1.5 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Icon className="h-3 w-3 text-muted-foreground flex-shrink-0" aria-hidden="true" />
          <span className={badge.className}>{badge.label}</span>
          {isPortfolio && (
            <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-semibold">
              Portfolio · {portfolioProperties.length} properties
            </span>
          )}
          {property.dstTitle && (
            <span className="text-[10px] text-muted-foreground truncate max-w-[160px]">
              {property.dstTitle}
            </span>
          )}
        </div>

        <Input
          value={property.address}
          onChange={(e) => onUpdate({ ...property, address: e.target.value })}
          placeholder={`Property ${index + 1} address or description`}
          disabled={disabled}
          aria-label={`Property ${index + 1} address`}
          className="text-sm border-blue-300 dark:border-blue-600 focus-visible:ring-blue-400"
        />

        {isLikeKind && (
          <div className="space-y-1.5 pt-1 border-t border-blue-200/60 dark:border-blue-800/60">
            <div className="grid grid-cols-2 gap-1.5">
              <div className="space-y-1">
                <label
                  htmlFor={`prop-common-name-${index}`}
                  className="block text-[10px] font-medium text-blue-700 dark:text-blue-300"
                >
                  Common name (optional)
                </label>
                <Input
                  id={`prop-common-name-${index}`}
                  value={property.commonName ?? ''}
                  onChange={(e) => onUpdate({ ...property, commonName: e.target.value })}
                  placeholder="e.g. Oak Street Duplex"
                  disabled={disabled}
                  aria-label={`Property ${index + 1} common name`}
                  className="text-sm border-blue-300 dark:border-blue-600 focus-visible:ring-blue-400"
                />
              </div>
              <div className="space-y-1">
                <label
                  htmlFor={`prop-type-select-${index}`}
                  className="block text-[10px] font-medium text-blue-700 dark:text-blue-300"
                >
                  Property type
                </label>
                <select
                  id={`prop-type-select-${index}`}
                  value={property.description ?? ''}
                  onChange={(e) => onUpdate({ ...property, description: e.target.value })}
                  disabled={disabled}
                  aria-label={`Property ${index + 1} type`}
                  className="w-full px-3 py-1.5 text-sm border border-blue-300 dark:border-blue-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 bg-card text-foreground"
                >
                  <option value="">Select type</option>
                  {PROPERTY_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label
                htmlFor={`prop-legal-desc-${index}`}
                className="block text-[10px] font-medium text-blue-700 dark:text-blue-300"
              >
                Legal description (optional)
              </label>
              <Input
                id={`prop-legal-desc-${index}`}
                value={property.legalDescription ?? ''}
                onChange={(e) => onUpdate({ ...property, legalDescription: e.target.value })}
                placeholder="e.g. Lot 7, Block 3, Maple Heights Subdivision"
                disabled={disabled}
                aria-label={`Property ${index + 1} legal description`}
                className="text-sm border-blue-300 dark:border-blue-600 focus-visible:ring-blue-400"
              />
            </div>

            <div className="space-y-1">
              <label
                htmlFor={`prop-parcel-${index}`}
                className="block text-[10px] font-medium text-blue-700 dark:text-blue-300"
              >
                Tax parcel number (optional)
              </label>
              <Input
                id={`prop-parcel-${index}`}
                value={property.taxParcelNumber ?? ''}
                onChange={(e) => onUpdate({ ...property, taxParcelNumber: e.target.value })}
                placeholder="e.g. 123-456-789"
                disabled={disabled}
                aria-label={`Property ${index + 1} tax parcel number`}
                className="text-sm border-blue-300 dark:border-blue-600 focus-visible:ring-blue-400"
              />
            </div>

            <div className="space-y-1">
              <label
                htmlFor={`prop-city-county-state-${index}`}
                className="block text-[10px] font-medium text-blue-700 dark:text-blue-300"
              >
                City / County / State
              </label>
              <Input
                id={`prop-city-county-state-${index}`}
                value={property.cityCountyState ?? ''}
                onChange={(e) => onUpdate({ ...property, cityCountyState: e.target.value })}
                placeholder="e.g. Portland / Multnomah / OR"
                disabled={disabled}
                aria-label={`Property ${index + 1} city county state`}
                className="text-sm border-blue-300 dark:border-blue-600 focus-visible:ring-blue-400"
              />
            </div>

            <div className="flex items-center gap-2 pt-0.5">
              <button
                type="button"
                onClick={() => setPartialAcquisition((v) => !v)}
                disabled={disabled}
                aria-label="Toggle partial acquisition"
                aria-expanded={partialAcquisition}
                className="flex items-center gap-1 text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
              >
                {partialAcquisition
                  ? <ChevronUp className="h-3 w-3" aria-hidden="true" />
                  : <ChevronDown className="h-3 w-3" aria-hidden="true" />}
                Acquiring less than 100%
              </button>
            </div>

            {partialAcquisition && (
              <div className="grid grid-cols-2 gap-1.5">
                <div className="space-y-1">
                  <label
                    htmlFor={`prop-pct-interest-${index}`}
                    className="block text-[10px] font-medium text-blue-700 dark:text-blue-300"
                  >
                    Percentage interest (%)
                  </label>
                  <Input
                    id={`prop-pct-interest-${index}`}
                    type="number"
                    min={0}
                    max={100}
                    step={0.01}
                    value={property.percentageInterest ?? ''}
                    onChange={(e) =>
                      onUpdate({ ...property, percentageInterest: parseFloat(e.target.value) || undefined })
                    }
                    placeholder="e.g. 50"
                    disabled={disabled}
                    aria-label={`Property ${index + 1} percentage interest`}
                    className="text-sm border-blue-300 dark:border-blue-600 focus-visible:ring-blue-400"
                  />
                </div>
                <div className="space-y-1">
                  <label
                    htmlFor={`prop-dollar-acquired-${index}`}
                    className="block text-[10px] font-medium text-blue-700 dark:text-blue-300"
                  >
                    Dollar value acquired ($)
                  </label>
                  <div className="relative flex items-center">
                    <DollarSign
                      className="absolute left-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none"
                      aria-hidden="true"
                    />
                    <Input
                      id={`prop-dollar-acquired-${index}`}
                      type="number"
                      value={property.dollarValueAcquired ?? ''}
                      onChange={(e) =>
                        onUpdate({ ...property, dollarValueAcquired: parseFloat(e.target.value) || undefined })
                      }
                      placeholder="0"
                      disabled={disabled}
                      aria-label={`Property ${index + 1} dollar value acquired`}
                      className="text-sm pl-7 border-blue-300 dark:border-blue-600 focus-visible:ring-blue-400"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {isPortfolio && (() => {
          const selectedAddresses = property.selectedPropertyAddresses ?? portfolioProperties.map((p) => p.address)
          const selectedCount = selectedAddresses.length
          return (
            <div className="space-y-1">
              <p className="text-[10px] font-medium text-muted-foreground">
                Underlying properties — investing in {selectedCount} of {portfolioProperties.length}
              </p>
              <ul className="ml-1 space-y-0.5">
                {portfolioProperties.map((portfolioProperty, portfolioIndex) => {
                  const checked = selectedAddresses.includes(portfolioProperty.address)
                  return (
                    <li key={portfolioIndex} className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        id={`prop-${index}-sub-${portfolioIndex}`}
                        checked={checked}
                        disabled={disabled}
                        aria-label={`Select underlying property ${portfolioProperty.address}`}
                        className="h-3.5 w-3.5 rounded border-border text-blue-600 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                        onChange={() => {
                          const next = checked
                            ? selectedAddresses.filter((a) => a !== portfolioProperty.address)
                            : [...selectedAddresses, portfolioProperty.address]
                          onUpdate({ ...property, selectedPropertyAddresses: next })
                        }}
                      />
                      <label
                        htmlFor={`prop-${index}-sub-${portfolioIndex}`}
                        className="text-[11px] text-muted-foreground truncate cursor-pointer"
                      >
                        {portfolioProperty.address}
                      </label>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })()}

        <div className="space-y-1">
          <label
            htmlFor={`prop-fmv-${index}`}
            className="block text-[10px] font-medium text-blue-700 dark:text-blue-300"
          >
            {isDst ? 'Your investment in this DST' : 'Estimated FMV'}
          </label>
          <div className="relative flex items-center">
            <DollarSign
              className="absolute left-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none"
              aria-hidden="true"
            />
            <Input
              id={`prop-fmv-${index}`}
              type="number"
              value={investmentValue || ''}
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0
                if (isDst) {
                  onUpdate({
                    ...property,
                    estimatedFMV: value,
                    investmentAmount: value,
                    allocationAmount: value,
                  })
                } else {
                  onUpdate({
                    ...property,
                    estimatedFMV: value,
                  })
                }
              }}
              placeholder="0"
              disabled={disabled}
              aria-label={`Property ${index + 1} estimated FMV`}
              className={cn(
                'text-sm pl-7 border-blue-300 dark:border-blue-600 focus-visible:ring-blue-400',
                investmentValue > 0
                  ? 'border-green-400 dark:border-green-600'
                  : ''
              )}
            />
          </div>
          {isDst && percentOfDst != null && (
            <p className="text-[10px] text-muted-foreground">
              {percentOfDst.toFixed(2)}% of ${(property.totalOfferingSize ?? 0).toLocaleString('en-US')} DST
            </p>
          )}
          {isDst && salesPrice != null && salesPrice > 0 && investmentValue > 0 && (
            <p className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">
              {((investmentValue / salesPrice) * 100).toFixed(1)}% of your exchange fund
            </p>
          )}
        </div>

        {showAllocationInput && !isDst && (
          <div className="space-y-1">
            <label
              htmlFor={`prop-alloc-${index}`}
              className="block text-[10px] font-medium text-blue-700 dark:text-blue-300"
            >
              Allocation
            </label>
            <div className="relative flex items-center">
              <DollarSign
                className="absolute left-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none"
                aria-hidden="true"
              />
              <Input
                id={`prop-alloc-${index}`}
                type="number"
                value={property.allocationAmount ?? ''}
                onChange={(e) =>
                  onUpdate({ ...property, allocationAmount: parseFloat(e.target.value) || 0 })
                }
                placeholder="0"
                disabled={disabled}
                aria-label={`Property ${index + 1} allocation amount`}
                className={cn(
                  'text-sm pl-7 border-blue-300 dark:border-blue-600 focus-visible:ring-blue-400',
                  property.allocationAmount && property.allocationAmount > 0
                    ? 'border-green-400 dark:border-green-600'
                    : ''
                )}
              />
            </div>
          </div>
        )}

        {isDst && property.description && (
          <p className="text-[10px] text-muted-foreground">{property.description}</p>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove property ${index + 1}`}
        className="flex-shrink-0 p-1 h-auto text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 mt-1"
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>
    </div>
  )
}
